import { authToken } from "@/services/firebase";

interface ApiEnvelope<T> {
  data: T;
  meta?: { requestId: string };
}
interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
    details?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code = "request_failed",
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api/v1";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await authToken();
  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const payload = (await response.json().catch(() => ({}))) as ApiEnvelope<T> &
    ApiErrorEnvelope;
  if (!response.ok)
    throw new ApiError(
      payload.error?.message || "The request could not be completed.",
      response.status,
      payload.error?.code,
      payload.error?.details,
    );
  return payload.data;
}

async function download(path: string) {
  const token = await authToken();
  const response = await fetch(`${baseUrl}${path}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    const payload = (await response
      .json()
      .catch(() => ({}))) as ApiErrorEnvelope;
    throw new ApiError(
      payload.error?.message || "The export could not be downloaded.",
      response.status,
      payload.error?.code,
    );
  }
  const disposition = response.headers.get("content-disposition") || "";
  const fileName =
    disposition.match(/filename="([^"]+)"/)?.[1] || "project-export";
  return { blob: await response.blob(), fileName };
}

async function downloadFile(path: string) {
  const token = await authToken();
  const response = await fetch(`${baseUrl}${path}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    const payload = (await response
      .json()
      .catch(() => ({}))) as ApiErrorEnvelope;
    throw new ApiError(
      payload.error?.message || "The file could not be downloaded.",
      response.status,
      payload.error?.code,
    );
  }
  const disposition = response.headers.get("content-disposition") || "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  return {
    blob: await response.blob(),
    fileName: encoded ? decodeURIComponent(encoded) : "project-file",
  };
}

export const api = {
  bootstrapDevelopmentIdentity: () =>
    request("/development/identity", { method: "POST", body: "{}" }),
  bootstrapInternalIdentity: (input: {
    microsoftAccessToken: string;
    microsoftIdToken: string;
  }) =>
    request("/internal-identity/bootstrap", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  session: () => request("/session"),
  workspaceSettings: () => request("/admin/settings"),
  directorySyncStatus: () => request("/admin/directory-sync"),
  runDirectorySync: () =>
    request("/admin/directory-sync", { method: "POST", body: "{}" }),
  emailTemplates: () => request("/admin/email-templates"),
  sendTestEmail: (email: string) =>
    request("/admin/email-test", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  testEmailStatus: (deliveryId: string) =>
    request(`/admin/email-test/${deliveryId}`),
  updateWorkspaceSettings: (input: unknown) =>
    request("/admin/settings", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  projects: () => request("/projects"),
  archivedProjects: () => request("/admin/projects/archived"),
  restoreArchivedProject: (projectId: string, input: unknown) =>
    request(`/admin/projects/${projectId}/restore`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  deleteArchivedProject: (projectId: string, input: unknown) =>
    request(`/admin/projects/${projectId}`, {
      method: "DELETE",
      body: JSON.stringify(input),
    }),
  myWork: () => request("/my-work"),
  organizations: () => request("/organizations"),
  createOrganization: (input: unknown) =>
    request("/organizations", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateOrganization: (organizationId: string, input: unknown) =>
    request(`/organizations/${organizationId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  search: (query = "") => request(`/search?query=${encodeURIComponent(query)}`),
  directoryUsers: (query = "", offset = 0, projectId = "") =>
    request(
      `/directory/users?query=${encodeURIComponent(query)}&offset=${encodeURIComponent(String(offset))}${projectId ? `&projectId=${encodeURIComponent(projectId)}` : ""}`,
    ),
  createProject: (input: unknown) =>
    request("/projects", { method: "POST", body: JSON.stringify(input) }),
  project: (projectId: string) => request(`/projects/${projectId}`),
  updateProject: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  previewProjectScheduleMove: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/project-schedule-preview`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  applyProjectScheduleMove: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/project-schedule-changes`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  projectBrief: (projectId: string) => request(`/projects/${projectId}/brief`),
  updateProjectBrief: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/brief`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  closeProject: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/close`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  archiveProject: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/archive`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  sections: (projectId: string) => request(`/projects/${projectId}/sections`),
  createSection: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/sections`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  tasks: (projectId: string) => request(`/projects/${projectId}/tasks`),
  dependencies: (projectId: string) =>
    request(`/projects/${projectId}/dependencies`),
  createDependency: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/dependencies`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  removeDependency: (projectId: string, dependencyId: string) =>
    request(`/projects/${projectId}/dependencies/${dependencyId}`, {
      method: "DELETE",
    }),
  participants: (projectId: string) =>
    request(`/projects/${projectId}/participants`),
  addParticipant: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/participants`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateParticipantRole: (projectId: string, actorId: string, input: unknown) =>
    request(
      `/projects/${projectId}/participants/${encodeURIComponent(actorId)}`,
      { method: "PATCH", body: JSON.stringify(input) },
    ),
  removeParticipant: (projectId: string, actorId: string) =>
    request(
      `/projects/${projectId}/participants/${encodeURIComponent(actorId)}`,
      { method: "DELETE" },
    ),
  invitations: (projectId: string) =>
    request(`/projects/${projectId}/invitations`),
  createInvitation: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/invitations`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  redeemInvitation: (input: unknown) =>
    request("/invitations/redeem", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  prepareInvitationMfa: (input: unknown) =>
    request("/invitations/mfa-preparation", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  resendInvitation: (projectId: string, invitationId: string) =>
    request(`/projects/${projectId}/invitations/${invitationId}/resend`, {
      method: "POST",
      body: "{}",
    }),
  revokeInvitation: (projectId: string, invitationId: string) =>
    request(`/projects/${projectId}/invitations/${invitationId}`, {
      method: "DELETE",
    }),
  files: (projectId: string) => request(`/projects/${projectId}/files`),
  risks: (projectId: string) => request(`/projects/${projectId}/risks`),
  createRisk: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/risks`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  activity: (projectId: string) => request(`/projects/${projectId}/activity`),
  notifications: () => request("/notifications"),
  notificationPreferences: () => request("/notification-preferences"),
  templates: () => request("/templates"),
  createTemplate: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/templates`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  exports: (projectId: string) => request(`/projects/${projectId}/exports`),
  createExport: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/exports`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  downloadExport: (jobId: string) => download(`/jobs/${jobId}/download`),
  updateNotificationPreferences: (input: unknown) =>
    request("/notification-preferences", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  updateTask: (projectId: string, taskId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks/${taskId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  createTask: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  previewTaskImport: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks/import-preview`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  applyTaskImport: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks/import`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  previewBulkTaskEdit: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks/bulk-edit-preview`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  applyBulkTaskEdit: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks/bulk-edit`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  deleteTasks: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks/delete`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  comment: (projectId: string, taskId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks/${taskId}/comments`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  deleteComment: (
    projectId: string,
    taskId: string,
    commentId: string,
    input: unknown,
  ) =>
    request(`/projects/${projectId}/tasks/${taskId}/comments/${commentId}`, {
      method: "DELETE",
      body: JSON.stringify(input),
    }),
  createFile: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/files`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  completeFileUpload: (projectId: string, fileId: string, input: unknown) =>
    request(`/projects/${projectId}/files/${fileId}/complete`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  abortFileUpload: (projectId: string, fileId: string, input: unknown) =>
    request(`/projects/${projectId}/files/${fileId}/abort`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  deleteFile: (projectId: string, fileId: string, input: unknown) =>
    request(`/projects/${projectId}/files/${fileId}`, {
      method: "DELETE",
      body: JSON.stringify(input),
    }),
  downloadFile: (projectId: string, fileId: string) =>
    downloadFile(`/projects/${projectId}/files/${fileId}/download`),
  previewSchedule: (projectId: string, taskId: string, input: unknown) =>
    request(`/projects/${projectId}/tasks/${taskId}/schedule-preview`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  applySchedule: (projectId: string, input: unknown) =>
    request(`/projects/${projectId}/schedule-changes`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  markNotification: (id: string) =>
    request(`/notifications/${id}/read`, { method: "PATCH", body: "{}" }),
};
